import { ThemeEvents, CartUpdateEvent } from '@theme/events';

/**
 * Gekühlter Versand — automatischer Pflicht-Zuschlag.
 *
 * Sobald ein kühlpflichtiges Produkt (Collection "kuehlversand", Tags Riegel/
 * Satisbites) im Warenkorb liegt, wird genau 1× das Produkt "Gekühlter Versand"
 * automatisch hinzugefügt. Liegt kein Kühlprodukt mehr im Korb, wird die Gebühr
 * wieder entfernt.
 *
 * Konfiguration kommt aus `window.__coldShipping` (gerendert in snippets/scripts.liquid).
 *
 * Hinweis: Reine Client-Logik (Basic-Plan erlaubt keine serverseitige
 * Validation-Function in Custom-Apps). Die Pflicht ist damit "weich" —
 * die Anzeige im Cart sperrt Menge/Entfernen, aber es ist keine
 * checkout-seitige Erzwingung.
 */

const CONFIG = window.__coldShipping || {};
// Theme setting "Gekühlter Versand". When off, we only clean up: a fee line
// left over in an existing cart gets removed, nothing is ever added.
const ENABLED = CONFIG.enabled !== false;
const FEE_VARIANT_ID = Number(CONFIG.feeVariantId) || 0;
const FEE_PRODUCT_ID = Number(CONFIG.feeProductId) || 0;
const COLD_PRODUCT_IDS = new Set((CONFIG.coldProductIds || []).map(Number));

if (FEE_VARIANT_ID && COLD_PRODUCT_IDS.size) {
  let busy = false;
  // Set when a cart change arrives while a reconcile is already running, so we
  // run one more pass afterwards instead of dropping it (avoids stale fee on
  // rapid successive cart changes).
  let pending = false;

  /** @returns {Promise<any>} the /cart.js payload */
  async function getCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    return response.json();
  }

  /** @param {any} cart */
  function cartHasColdProduct(cart) {
    return cart.items.some(
      (item) => item.product_id !== FEE_PRODUCT_ID && COLD_PRODUCT_IDS.has(item.product_id)
    );
  }

  /** @param {any} cart */
  function findFeeLine(cart) {
    return cart.items.find((item) => item.variant_id === FEE_VARIANT_ID);
  }

  async function addFee() {
    // Single-id form (NOT the `items: [...]` array form): the array form trips
    // an existing cart-rebuild behaviour on this shop (Satisbites Mix-Box /
    // cart transform) that drops the cold product. The single form is safe.
    await fetch(`${Theme.routes.cart_add_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: FEE_VARIANT_ID, quantity: 1 }),
    });
  }

  /**
   * @param {string} key - the cart line key
   * @param {number} quantity - target quantity (0 removes the line)
   */
  async function setFeeQuantity(key, quantity) {
    await fetch(`${Theme.routes.cart_change_url}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity }),
    });
  }

  /**
   * Brings the cold-shipping fee line in sync with the rest of the cart:
   * present (qty 1) when a cold product is in the cart, absent otherwise.
   */
  async function reconcileOnce() {
    let cart = await getCart();
    const hasCold = cartHasColdProduct(cart);
    const fee = findFeeLine(cart);
    let changed = false;

    if (!ENABLED) {
      if (fee) {
        await setFeeQuantity(fee.key, 0);
        changed = true;
      }
    } else if (hasCold) {
      if (!fee) {
        await addFee();
        changed = true;
      } else if (fee.quantity !== 1) {
        await setFeeQuantity(fee.key, 1);
        changed = true;
      }
    } else if (fee) {
      await setFeeQuantity(fee.key, 0);
      changed = true;
    }

    if (changed) {
      cart = await getCart();
      // No `sections` payload → every cart-items-component re-renders itself
      // via the section renderer, and cart-icon picks up the new item count.
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'cold-shipping', {
          source: 'cold-shipping',
          itemCount: cart.item_count,
        })
      );
    }
  }

  async function reconcile() {
    // Coalesce concurrent triggers: if one is running, mark a re-run instead of
    // dropping the trigger, then drain until no new change arrived mid-flight.
    if (busy) {
      pending = true;
      return;
    }
    busy = true;
    try {
      do {
        pending = false;
        await reconcileOnce();
      } while (pending);
    } catch (error) {
      console.error('[cold-shipping]', error);
    } finally {
      busy = false;
    }
  }

  // React to every cart change (add / quantity change / removal), but ignore the
  // update we dispatch ourselves to avoid a feedback loop.
  document.addEventListener(ThemeEvents.cartUpdate, (event) => {
    if (event.detail?.data?.source === 'cold-shipping') return;
    reconcile();
  });

  // Initial pass — covers page loads / direct cart visits where the fee may be
  // missing or stale (e.g. cold item left over from a previous session).
  if (document.readyState !== 'loading') {
    reconcile();
  } else {
    document.addEventListener('DOMContentLoaded', reconcile, { once: true });
  }
}
